import { db } from "../firebaseClient"
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore"
import { buildAudience, applyFilters, listSegments } from "./segmentHelpers"

// Audience is resolved to a snapshot list of learner userIds at publish
// time, not re-evaluated live — same reasoning as a Segment's matched-count
// being a point-in-time read: "who got this announcement" should stay a
// stable historical fact even if segment membership shifts afterward.
//
// NOTE: only "all" and "segment" are wired below. A "purchasers" audience
// type is in the vision doc but needs the orders object's field shape
// confirmed first (unseen) rather than guessed — see AnnouncementsPanel.
const resolveAudienceUserIds = async (orgId, audienceType, segmentId) => {
  const audience = await buildAudience(orgId)

  if (audienceType === "all") {
    return audience?.learners?.map((l) => l?.userId).filter(Boolean) || []
  }

  if (audienceType === "segment" && segmentId) {
    const { segments } = await listSegments(orgId)
    const segment = segments?.find((s) => s?.segmentId === segmentId)
    if (!segment) return []
    const matched = applyFilters(audience?.learners, segment?.filters || [], audience)
    return matched?.map((l) => l?.userId).filter(Boolean) || []
  }

  return []
}

export const createAnnouncement = async ({
  orgId,
  title,
  body,
  ctaLabel,
  ctaUrl,
  audienceType,
  segmentId,
  scheduledFor,
}) => {
  if (!orgId || !title?.trim() || !body?.trim()) {
    return { announcementId: null, error: { message: "Title and message body are required" } }
  }
  try {
    const ref = doc(collection(db, "organizations", orgId, "announcements"))
    await setDoc(ref, {
      title: title.trim(),
      body: body.trim(),
      ctaLabel: ctaLabel?.trim() || null,
      ctaUrl: ctaUrl?.trim() || null,
      audienceType: audienceType || "all", // "all" | "segment"
      segmentId: audienceType === "segment" ? segmentId || null : null,
      status: scheduledFor ? "scheduled" : "draft",
      scheduledFor: scheduledFor || null,
      audienceUserIds: [],
      audienceCount: 0,
      createdAt: serverTimestamp(),
      publishedAt: null,
    })
    return { announcementId: ref.id, error: null }
  } catch (error) {
    return { announcementId: null, error }
  }
}

export const updateAnnouncement = async (orgId, announcementId, fields) => {
  try {
    await updateDoc(doc(db, "organizations", orgId, "announcements", announcementId), fields)
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const deleteAnnouncement = async (orgId, announcementId) => {
  try {
    await deleteDoc(doc(db, "organizations", orgId, "announcements", announcementId))
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const listAnnouncements = async (orgId) => {
  try {
    const ref = collection(db, "organizations", orgId, "announcements")
    const q = query(ref, orderBy("createdAt", "desc"))
    const snap = await getDocs(q)
    return { announcements: snap.docs?.map((d) => ({ announcementId: d.id, ...d.data() })), error: null }
  } catch (error) {
    return { announcements: [], error }
  }
}

// Publishes immediately: resolves the audience snapshot right now and
// flips status to "published".
export const publishAnnouncement = async (orgId, announcementId, audienceType, segmentId) => {
  try {
    const audienceUserIds = await resolveAudienceUserIds(orgId, audienceType, segmentId)
    await updateDoc(doc(db, "organizations", orgId, "announcements", announcementId), {
      status: "published",
      audienceUserIds,
      audienceCount: audienceUserIds.length,
      publishedAt: serverTimestamp(),
    })
    return { audienceCount: audienceUserIds.length, error: null }
  } catch (error) {
    return { audienceCount: 0, error }
  }
}

// Self-heal pattern (same idea as ensureLearnerCountry / the users.createdAt
// backfill): there's no Cloud Function cron on the Spark plan, so a
// "scheduled" announcement only actually goes out the next time someone
// with access opens this panel after its scheduledFor time has passed.
// AnnouncementsPanel calls this once on load, before rendering the list.
export const publishDueScheduledAnnouncements = async (orgId) => {
  try {
    const { announcements } = await listAnnouncements(orgId)
    const now = new Date()
    const due = announcements?.filter((a) => {
      if (a?.status !== "scheduled" || !a?.scheduledFor) return false
      const scheduledDate = a.scheduledFor?.toDate ? a.scheduledFor.toDate() : new Date(a.scheduledFor)
      return scheduledDate <= now
    })

    for (const a of due || []) {
      await publishAnnouncement(orgId, a.announcementId, a.audienceType, a.segmentId)
    }

    return { publishedCount: due?.length || 0, error: null }
  } catch (error) {
    return { publishedCount: 0, error }
  }
}